import { makeAutoObservable } from "mobx";

import {
  isGlobalProcessType,
  PROCESS_SCOPE,
  PROCESS_STATUS,
} from "@/store/subscriptionCore/processConstants";
import { IS_DEV } from "@/utils/env";

const MAX_NOTIFICATION_QUEUE = 20;
const PROCESS_TRACKING_TAG = "[Process Tracking]";

/** Max completed (done/error) runs retained per process key; active runs are never pruned. */
const MAX_TERMINAL_ENTRIES_PER_PROCESS_KEY = 25;

/**
 * @param {string} type
 * @param {string | null | undefined} propertyId
 * @param {string} scope
 * @returns {string}
 */
function toProcessKey(type, propertyId, scope) {
  if (scope === PROCESS_SCOPE.PROPERTY) {
    return `${type}::${normalizePropertyId(propertyId) ?? ""}`;
  }
  return `${type}::global`;
}

/**
 * @param {string | null | undefined} propertyId
 * @returns {string | null}
 */
function normalizePropertyId(propertyId) {
  if (propertyId == null) return null;
  const normalized = String(propertyId).trim();
  return normalized.length > 0 ? normalized : null;
}

/**
 * Drops oldest terminal entries for a key so `_entries` does not grow without bound.
 * ASSUMPTION: Only entries with status done/error are removable; callers delete active from `_activeByKey` first.
 *
 * @param {Map<string, Object>} entries
 * @param {string} processKey
 */
function pruneTerminalEntriesForKey(entries, processKey) {
  const terminalStatuses = new Set([PROCESS_STATUS.DONE, PROCESS_STATUS.ERROR]);
  const forKey = [...entries.values()].filter((e) => e.key === processKey && terminalStatuses.has(e.status));
  if (forKey.length <= MAX_TERMINAL_ENTRIES_PER_PROCESS_KEY) return;
  forKey.sort((a, b) => (b.startedAt ?? 0) - (a.startedAt ?? 0));
  const excess = forKey.slice(MAX_TERMINAL_ENTRIES_PER_PROCESS_KEY);
  for (const e of excess) {
    entries.delete(e.id);
  }
}

function toNotification(input) {
  return {
    id: `${Date.now()}_${Math.random().toString(36).slice(2, 9)}`,
    processType: input.processType,
    outcome: input.outcome,
    message: input.message,
    propertyId: input.propertyId ?? null,
    actionRoute: input.actionRoute ?? null,
    actionLabel: input.actionLabel ?? null,
    timestamp: Date.now(),
  };
}

/**
 * @param {string} type
 * @returns {string}
 */
function toProcessTag(type) {
  const normalized = String(type ?? "unknown")
    .replace(/([a-z])([A-Z])/g, "$1_$2")
    .replace(/[^a-zA-Z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "")
    .toUpperCase();
  return normalized || "UNKNOWN";
}

/**
 * @param {string} message
 * @param {Object} [payload]
 * @returns {void}
 */
function logProcessTracking(message, payload = undefined) {
  if (!IS_DEV) return;
  if (payload === undefined) {
    console.log(message);
    return;
  }
  console.log(message, payload);
}

/**
 * Owns in-flight and recent completed process runs. Notifications are capped separately;
 * completed runs are pruned per process key so `_entries` stays bounded (see prune helper).
 */
class ProcessTrackerStore {
  _entries = new Map();
  _activeByKey = new Map();
  _pendingNotifications = [];
  _handlers = new Map();
  /** @type {Set<(payload: Object) => void>} Success-only completion listeners. */
  _completionListeners = new Set();
  /** @type {string} Last customer id passed to {@link #syncPersistenceContext} (reserved for future persistence). */
  _persistenceCustomer = "";
  /** @type {string} Last user id passed to {@link #syncPersistenceContext} (reserved for future persistence). */
  _persistenceUserId = "";

  constructor() {
    makeAutoObservable(this, {}, { autoBind: true });
  }

  /**
   * Keeps tenant/user context aligned with auth for any future tracker persistence (no-op beyond field update today).
   *
   * @param {{ customer?: string|null, userId?: string|null }} [ctx]
   */
  syncPersistenceContext(ctx = {}) {
    this._persistenceCustomer = String(ctx.customer ?? "").trim();
    this._persistenceUserId = String(ctx.userId ?? "").trim();
  }

  /**
   * Subscribe to **successful** process completions only (`status` becomes done, handler resolved, not skipped/cancelled).
   * Payload shape: `{ type, output, process, propertyId }` where `process` is the terminal entry snapshot from `_entries`.
   *
   * @param {(payload: { type: string, output: Object, process: Object|null, propertyId: string|null }) => void} listener
   * @returns {() => void} Unsubscribe function.
   */
  subscribeProcessCompletion(listener) {
    if (typeof listener !== "function") {
      return () => {};
    }
    this._completionListeners.add(listener);
    return () => {
      this._completionListeners.delete(listener);
    };
  }

  /**
   * @param {string} type
   * @param {string|null} propertyId
   * @param {string} processId
   * @param {Object} output
   */
  _notifyProcessCompletionSuccess(type, propertyId, processId, output) {
    const process = this._entries.get(processId) ?? null;
    const payload = { type, output, process, propertyId };
    for (const fn of [...this._completionListeners]) {
      try {
        fn(payload);
      } catch (err) {
        if (IS_DEV) {
          console.error(`${PROCESS_TRACKING_TAG} completion listener error`, err);
        }
      }
    }
  }

  /**
   * @param {string} type
   * @param {(params: Object, context: Object) => Promise<Object>} handler
   */
  registerHandler(type, handler) {
    logProcessTracking(`${PROCESS_TRACKING_TAG} [${toProcessTag(type)}] Registered handler`, { type });
    this._handlers.set(type, handler);
  }

  /**
   * @param {string} type
   * @param {Object} [options]
   * @returns {Object}
   */
  async startProcess(type, options = {}) {
    const processTag = toProcessTag(type);
    const normalizedPropertyId = normalizePropertyId(options.propertyId);
    const scope = options.scope ?? (normalizedPropertyId ? PROCESS_SCOPE.PROPERTY : PROCESS_SCOPE.GLOBAL);
    const processKey = toProcessKey(type, normalizedPropertyId, scope);
    logProcessTracking(`${PROCESS_TRACKING_TAG} [${processTag}] Start requested`, {
      processKey,
      scope,
      propertyId: normalizedPropertyId,
    });
    const existingId = this._activeByKey.get(processKey);
    if (existingId) {
      const existing = this._entries.get(existingId);
      if (existing && [PROCESS_STATUS.INVOKING, PROCESS_STATUS.PROCESSING].includes(existing.status)) {
        logProcessTracking(`${PROCESS_TRACKING_TAG} [${processTag}] Start skipped due to active process`, {
          processId: existing.id,
          status: existing.status,
          processKey,
        });
        return { skipped: true, process: existing };
      }
    }

    const handler = options.handler ?? this._handlers.get(type);
    if (typeof handler !== "function") {
      throw new Error(`No process handler registered for type "${type}"`);
    }

    const processId = `${type}_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`;
    const entry = {
      id: processId,
      key: processKey,
      type,
      propertyId: normalizedPropertyId,
      correlationId: null,
      status: PROCESS_STATUS.INVOKING,
      progressMessage: "",
      progressStage: "",
      metadata: options.metadata ?? {},
      result: null,
      error: null,
      startedAt: Date.now(),
      completedAt: null,
      teardown: null,
      cancelled: false,
    };

    this._entries.set(processId, entry);
    this._activeByKey.set(processKey, processId);
    logProcessTracking(`${PROCESS_TRACKING_TAG} [${processTag}] Process created`, {
      processId,
      processKey,
      status: entry.status,
    });

    const updateEntry = (patch) => {
      const live = this._entries.get(processId);
      if (!live) return;
      this._entries.set(processId, { ...live, ...patch });
    };

    const context = {
      /** Subject property for property-scoped runs (e.g. ADD_COMP); handlers may use for CTAs. */
      propertyId: normalizedPropertyId,
      setCorrelationId: (correlationId) => {
        updateEntry({ correlationId: correlationId ?? null });
        logProcessTracking(`${PROCESS_TRACKING_TAG} [${processTag}] Correlation id set`, {
          processId,
          correlationId: correlationId ?? null,
        });
      },
      setProgress: ({ status, message, stage }) => {
        const next = {};
        if (status) next.status = status;
        if (typeof message === "string") next.progressMessage = message;
        if (typeof stage === "string") next.progressStage = stage;
        updateEntry(next);
        logProcessTracking(`${PROCESS_TRACKING_TAG} [${processTag}] Progress updated`, {
          processId,
          status: next.status ?? null,
          message: next.progressMessage ?? null,
          stage: next.progressStage ?? null,
        });
      },
      registerTeardown: (teardown) => {
        if (typeof teardown === "function") {
          updateEntry({ teardown });
          logProcessTracking(`${PROCESS_TRACKING_TAG} [${processTag}] Teardown registered`, { processId });
        }
      },
      isCancelled: () => {
        const live = this._entries.get(processId);
        return !live || live.cancelled === true;
      },
    };

    try {
      const output = await handler(options.params ?? {}, context);
      if (context.isCancelled()) {
        logProcessTracking(`${PROCESS_TRACKING_TAG} [${processTag}] Handler resolved after cancellation`, { processId });
        return { cancelled: true };
      }
      updateEntry({
        status: PROCESS_STATUS.DONE,
        result: output?.payload ?? output ?? null,
        completedAt: Date.now(),
      });
      this._activeByKey.delete(processKey);
      pruneTerminalEntriesForKey(this._entries, processKey);
      logProcessTracking(`${PROCESS_TRACKING_TAG} [${processTag}] Process completed`, {
        processId,
        status: PROCESS_STATUS.DONE,
      });

      this._notifyProcessCompletionSuccess(type, normalizedPropertyId, processId, output);

      if (typeof options.onComplete === "function") {
        await options.onComplete(output);
      }

      const notification = output?.notification ?? null;
      if (notification) {
        logProcessTracking(`${PROCESS_TRACKING_TAG} [${processTag}] Enqueueing success notification`, {
          processId,
          message: notification.message ?? null,
          outcome: notification.outcome ?? null,
        });
        this.enqueueNotification(
          toNotification({
            processType: type,
            propertyId: normalizedPropertyId,
            ...notification,
          })
        );
      }

      return { success: true, process: this._entries.get(processId), output };
    } catch (error) {
      if (context.isCancelled()) {
        logProcessTracking(`${PROCESS_TRACKING_TAG} [${processTag}] Handler errored after cancellation`, { processId });
        return { cancelled: true };
      }
      updateEntry({
        status: PROCESS_STATUS.ERROR,
        error: error?.message ?? "Process failed",
        completedAt: Date.now(),
      });
      this._activeByKey.delete(processKey);
      pruneTerminalEntriesForKey(this._entries, processKey);
      logProcessTracking(`${PROCESS_TRACKING_TAG} [${processTag}] Process failed`, {
        processId,
        error: error?.message ?? "Process failed",
      });

      if (options.notifyOnError !== false) {
        logProcessTracking(`${PROCESS_TRACKING_TAG} [${processTag}] Enqueueing error notification`, {
          processId,
          message: options.errorMessage ?? (error?.message || "Process failed"),
        });
        this.enqueueNotification(
          toNotification({
            processType: type,
            outcome: "error",
            message: options.errorMessage ?? (error?.message || "Process failed"),
            propertyId: normalizedPropertyId,
          })
        );
      }
      throw error;
    }
  }

  enqueueNotification(notification) {
    logProcessTracking(`${PROCESS_TRACKING_TAG} [${toProcessTag(notification?.processType)}] Notification queued`, {
      outcome: notification?.outcome ?? null,
      message: notification?.message ?? null,
      propertyId: notification?.propertyId ?? null,
    });
    this._pendingNotifications = [...this._pendingNotifications, notification];
    if (this._pendingNotifications.length > MAX_NOTIFICATION_QUEUE) {
      this._pendingNotifications = this._pendingNotifications.slice(-MAX_NOTIFICATION_QUEUE);
    }
  }

  consumeNotification() {
    if (this._pendingNotifications.length === 0) return null;
    const [first, ...rest] = this._pendingNotifications;
    this._pendingNotifications = rest;
    logProcessTracking(`${PROCESS_TRACKING_TAG} [${toProcessTag(first?.processType)}] Notification consumed`, {
      outcome: first?.outcome ?? null,
      message: first?.message ?? null,
      propertyId: first?.propertyId ?? null,
    });
    return first;
  }

  get pendingNotifications() {
    return this._pendingNotifications;
  }

  get activeProcesses() {
    return [...this._entries.values()].filter((entry) =>
      [PROCESS_STATUS.INVOKING, PROCESS_STATUS.PROCESSING].includes(entry.status)
    );
  }

  getPropertyProcess(type, propertyId) {
    const normalizedPropertyId = normalizePropertyId(propertyId);
    if (normalizedPropertyId == null) return null;
    const key = toProcessKey(type, normalizedPropertyId, PROCESS_SCOPE.PROPERTY);
    const id = this._activeByKey.get(key);
    return id ? (this._entries.get(id) ?? null) : null;
  }

  /**
   * Most recently started matching run for a property-scoped process type.
   *
   * @param {string} type
   * @param {string} propertyId
   * @returns {Object|null}
   */
  getLatestPropertyProcess(type, propertyId) {
    const normalizedPropertyId = normalizePropertyId(propertyId);
    if (normalizedPropertyId == null) return null;
    const filtered = [...this._entries.values()].filter(
      (entry) => entry.type === type && normalizePropertyId(entry.propertyId) === normalizedPropertyId
    );
    if (filtered.length === 0) return null;
    return filtered.sort((a, b) => (b.startedAt ?? 0) - (a.startedAt ?? 0))[0];
  }

  /**
   * Most recently started matching global run for `type` (entries with null property id).
   *
   * @param {string} type
   * @returns {Object|null}
   */
  getLatestGlobalProcess(type) {
    const filtered = [...this._entries.values()].filter((entry) => entry.type === type && entry.propertyId == null);
    if (filtered.length === 0) return null;
    return filtered.sort((a, b) => (b.startedAt ?? 0) - (a.startedAt ?? 0))[0];
  }

  /**
   * Most recently started matching run (by `startedAt`), including terminal done/error entries.
   * Not the same as “no work in flight”: after completion, `status` remains done/error until pruned.
   * Use {@link #isProcessRunning} or hook `phase` / `isRunning` when you need active vs settled.
   *
   * @param {string} type
   * @param {string|null} [propertyId] - Null now means "global" only; use `getLatestPropertyProcess` for property scope
   * @returns {Object|null}
   */
  getLatestProcess(type, propertyId = null) {
    const normalizedPropertyId = normalizePropertyId(propertyId);
    if (normalizedPropertyId == null) {
      if (IS_DEV && !isGlobalProcessType(type)) {
        // Helps catch accidental cross-property lookups in UI hooks.
        console.warn(`ProcessTrackerStore.getLatestProcess("${type}") called without propertyId; using global scope only.`);
      }
      return this.getLatestGlobalProcess(type);
    }
    return this.getLatestPropertyProcess(type, normalizedPropertyId);
  }

  /**
   * Waits until the latest matching run reaches a terminal state (done/error), or timeout.
   *
   * @param {string} type
   * @param {string|null} propertyId
   * @param {{timeoutMs?: number, pollMs?: number}} [options]
   * @returns {Promise<Object|null>}
   */
  async waitForTerminalProcess(type, propertyId, options = {}) {
    const processTag = toProcessTag(type);
    const normalizedPropertyId = normalizePropertyId(propertyId);
    const timeoutMs = Number.isFinite(options.timeoutMs) ? options.timeoutMs : 30_000;
    const pollMs = Number.isFinite(options.pollMs) ? options.pollMs : 100;
    const startedAt = Date.now();
    logProcessTracking(`${PROCESS_TRACKING_TAG} [${processTag}] Waiting for terminal process`, {
      propertyId: normalizedPropertyId,
      timeoutMs,
      pollMs,
    });

    while (Date.now() - startedAt <= timeoutMs) {
      const latest =
        normalizedPropertyId == null
          ? this.getLatestGlobalProcess(type)
          : this.getLatestPropertyProcess(type, normalizedPropertyId);
      if (!latest) return null;
      if ([PROCESS_STATUS.DONE, PROCESS_STATUS.ERROR].includes(latest.status)) {
        logProcessTracking(`${PROCESS_TRACKING_TAG} [${processTag}] Terminal process observed`, {
          processId: latest.id,
          status: latest.status,
          propertyId: latest.propertyId ?? null,
        });
        return latest;
      }
      await new Promise((resolve) => {
        setTimeout(resolve, pollMs);
      });
    }

    throw new Error(`Timed out waiting for process "${type}" to complete.`);
  }

  isProcessRunning(type, propertyId = null) {
    const normalizedPropertyId = normalizePropertyId(propertyId);
    if (normalizedPropertyId != null) {
      const key = toProcessKey(type, normalizedPropertyId, PROCESS_SCOPE.PROPERTY);
      const id = this._activeByKey.get(key);
      if (!id) return false;
      const entry = this._entries.get(id);
      return Boolean(entry && [PROCESS_STATUS.INVOKING, PROCESS_STATUS.PROCESSING].includes(entry.status));
    }
    return this.activeProcesses.some((entry) => entry.type === type);
  }

  cancelProcess(processId) {
    const entry = this._entries.get(processId);
    if (!entry) return;
    const processTag = toProcessTag(entry.type);
    const teardown = typeof entry.teardown === "function" ? entry.teardown : null;
    if (teardown) {
      teardown();
    }
    const nextEntry = {
      ...entry,
      cancelled: true,
      teardown: null,
      status: PROCESS_STATUS.ERROR,
      error: "Cancelled",
      completedAt: Date.now(),
    };
    this._entries.set(processId, nextEntry);
    this._activeByKey.delete(nextEntry.key);
    pruneTerminalEntriesForKey(this._entries, nextEntry.key);
    logProcessTracking(`${PROCESS_TRACKING_TAG} [${processTag}] Process cancelled`, {
      processId,
      propertyId: nextEntry.propertyId ?? null,
    });
  }

  reset() {
    logProcessTracking(`${PROCESS_TRACKING_TAG} [SYSTEM] Resetting process tracker store`);
    for (const entry of this._entries.values()) {
      if (typeof entry.teardown === "function") {
        entry.teardown();
      }
    }
    this._entries = new Map();
    this._activeByKey = new Map();
    this._pendingNotifications = [];
    this._persistenceCustomer = "";
    this._persistenceUserId = "";
  }

  resetForNewCustomer() {
    this.reset();
  }
}

export default ProcessTrackerStore;
